import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { AdHocSectionController } from "../../controllers/ad-hoc-section-controller";
import { AdverseEventSectionController } from "../../controllers/adverse-event-section-controller";
import { BenefitRelatedDefaultSectionController } from "../../controllers/benefit-related-default-section-controller";
import { CaseIdContainerSectionController } from "../../controllers/case-id-container-section-controller";
import { CoverageDecisionSectionController } from "../../controllers/coverage-decision-section-controller";
import { MedicalSummarySectionController } from "../../controllers/medical-summary-section-controller";
import { NegotiationRoundAdHocSectionController } from "../../controllers/negotiation-round-ad-hoc-section-controller";
import { PreparationSectionController } from "../../controllers/preparation-section-controller";
import { PrescriptionSectionController } from "../../controllers/prescription-section-controller";
import { PricePreviewSectionController } from "../../controllers/price-preview-section-controler";
import { PrimaryInterventionDateRelatedDefaultSectionController } from "../../controllers/primary-intervention-date-related-default-section-controller";
import { RequestReceiptDateRelatedDefaultSectionController } from "../../controllers/request-receipt-date-related-default-section-controller";
import { ResponseRelatedDefaultSectionController } from "../../controllers/response-related-default-section-controller";
import { SupportingItemSectionController } from "../../controllers/supporting-item-section-controller";
import { TreatingPhysicianSectionController } from "../../controllers/treating-physician-section-controller";
import { FlexEntityType } from "../generated/graphql";
import { FlexFieldController } from "./flex-field-controller";
import { FlexSectionController } from "./flex-section-controller";
import { FlexSectionSchema } from "./flex-section-schema";

const continuousBenefitTypeId = "CB-0000000003";
const medicalSummarySectionId = "medical-summary";

export const sectionControllerFactory = (
	type: FlexEntityType | undefined,
	section: FlexSectionSchema,
	path: string,
	fieldControllers: Array<FlexFieldController>,
	sectionControllers: Array<FlexSectionController>
): FlexSectionController => {
	if (section.localizableKey === medicalSummarySectionId) {
		return new MedicalSummarySectionController(section, path, fieldControllers, sectionControllers);
	}

	if (
		section.fields.some(
			(field) =>
				field.attribute.identifier === AttributeIdentifiers.RequestReceiptDate ||
				field.attribute.identifier === AttributeIdentifiers.DiagnosisDate
		)
	) {
		return new RequestReceiptDateRelatedDefaultSectionController(section, path, fieldControllers, sectionControllers);
	}

	if (section.fields.some((field) => field.attribute.identifier === AttributeIdentifiers.ResponseAssessmentDate)) {
		return new ResponseRelatedDefaultSectionController(section, path, fieldControllers, sectionControllers);
	}

	if (section.fields.some((field) => field.attribute.identifier === AttributeIdentifiers.BenefitAssessmentDate)) {
		return new BenefitRelatedDefaultSectionController(section, path, fieldControllers, sectionControllers);
	}

	if (section.fields.some((field) => field.attribute.identifier === AttributeIdentifiers.PrimaryInterventionDate)) {
		return new PrimaryInterventionDateRelatedDefaultSectionController(section, path, fieldControllers, sectionControllers);
	}

	if (section.fields.some((field) => field.attribute.identifier === AttributeIdentifiers.TreatingPhysicianTitle)) {
		return new TreatingPhysicianSectionController(section, path, fieldControllers, sectionControllers);
	}

	if (section.fields.some((field) => field.attribute.identifier === AttributeIdentifiers.DecisionTherapy)) {
		return new CoverageDecisionSectionController(section, path, fieldControllers, sectionControllers);
	}

	if (section.fields.some((field) => field.attribute.identifier === AttributeIdentifiers.AdverseEvent)) {
		return new AdverseEventSectionController(section, path, fieldControllers, sectionControllers);
	}

	if (section.fields.some((field) => field.attribute.identifier === AttributeIdentifiers.BrandName)) {
		return new PreparationSectionController(section, path, fieldControllers, sectionControllers);
	}

	if (
		type?.id === continuousBenefitTypeId &&
		section.sections?.some((inner) => inner.fields.some((field) => field.attribute.identifier === AttributeIdentifiers.TreatmentPack))
	) {
		return new PrescriptionSectionController(section, path, fieldControllers, sectionControllers);
	}

	if (section.fields.some((field) => field.attribute.identifier === AttributeIdentifiers.SupportingItemModel)) {
		return new SupportingItemSectionController(section, path, fieldControllers, sectionControllers);
	}

	if (section.position === 0 && !section.fields.some((field) => field.attribute.identifier === AttributeIdentifiers.PurchaseId)) {
		return new CaseIdContainerSectionController(section, path, fieldControllers, sectionControllers);
	}

	if (
		section.fields.some(
			(field) =>
				field.attribute.identifier === AttributeIdentifiers.SuggestedDiscountRate ||
				field.attribute.identifier === AttributeIdentifiers.DiscountRate
		)
	) {
		return new AdHocSectionController(section, path, fieldControllers, sectionControllers);
	}

	if (section.fields.some((field) => field.attribute.identifier === AttributeIdentifiers.DiscountRate)) {
		return new NegotiationRoundAdHocSectionController(section, path, fieldControllers, sectionControllers);
	}

	if (section.fields.some((field) => field.attribute.identifier === AttributeIdentifiers.PricePreview)) {
		return new PricePreviewSectionController(section, path, fieldControllers, sectionControllers);
	}

	return {
		schema: section,
		path,
		fieldControllers: fieldControllers,
		sectionControllers: sectionControllers,
	} as FlexSectionController;
};
